import { Plus, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { TimeSelect } from "@/components/TimeSelect";
import {
  DAYS,
  DEFAULT_SLOT,
  type Day,
  type DayState,
  type TimeSlot,
  type ScheduleFormState,
} from "@/lib/utils/schedule";

const DAY_LABELS: Record<string, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

type ScheduleInputProps = {
  value: ScheduleFormState;
  onChange: (v: ScheduleFormState) => void;
  errors?: Record<string, string>;
  defaultSlot?: TimeSlot;
};

export function ScheduleInput({
  value,
  onChange,
  errors = {},
  defaultSlot = DEFAULT_SLOT,
}: ScheduleInputProps) {
  function updateDay(day: Day, patch: Partial<DayState>) {
    onChange({ ...value, [day]: { ...value[day], ...patch } });
  }

  function updateSlot(day: Day, index: number, patch: Partial<TimeSlot>) {
    const slots = value[day].slots.map((s, i) => (i === index ? { ...s, ...patch } : s));
    updateDay(day, { slots });
  }

  function addSlot(day: Day) {
    const slots = [...value[day].slots, { ...defaultSlot }];
    updateDay(day, { slots });
  }

  function removeSlot(day: Day, index: number) {
    const slots = value[day].slots.filter((_, i) => i !== index);
    updateDay(day, { slots });
  }

  function toggleDay(day: Day) {
    const d = value[day];
    if (d.enabled) {
      updateDay(day, { enabled: false });
    } else {
      let slots: TimeSlot[];
      if (d.slots.length > 0) {
        slots = d.slots;
      } else {
        const dayIndex = DAYS.indexOf(day);
        const prevDay = dayIndex > 0 ? DAYS[dayIndex - 1] : null;
        const prevState = prevDay ? value[prevDay] : null;
        slots =
          prevState && prevState.enabled && prevState.slots.length > 0
            ? prevState.slots.map((s) => ({ ...s }))
            : [{ ...defaultSlot }];
      }
      updateDay(day, { enabled: true, slots });
    }
  }

  return (
    <div className="space-y-3">
      {DAYS.map((day) => {
        const d = value[day];
        return (
          <div key={day}>
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={d.enabled}
                onCheckedChange={() => toggleDay(day)}
                aria-label={`Toggle ${DAY_LABELS[day]}`}
              />
              <span className="text-sm w-24 shrink-0">{DAY_LABELS[day]}</span>
              {!d.enabled && <span className="text-sm text-muted-foreground">Closed</span>}
            </label>

            {d.enabled && (
              <div className="sm:ml-6 mt-1.5 space-y-1.5">
                {d.slots.map((slot, i) => (
                  <div key={i} className="flex items-center gap-2 flex-wrap">
                    <TimeSelect
                      value={slot.open}
                      onChange={(value) => updateSlot(day, i, { open: value })}
                    />
                    <span className="text-muted-foreground text-sm shrink-0">–</span>
                    <TimeSelect
                      value={slot.close}
                      onChange={(value) => updateSlot(day, i, { close: value })}
                    />
                    {d.slots.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSlot(day, i)}
                        className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                        aria-label="Remove slot"
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => addSlot(day)}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  <Plus className="size-3" />
                  Add hours
                </button>
              </div>
            )}
            {errors[day] && <p className="text-xs text-destructive mt-1">{errors[day]}</p>}
          </div>
        );
      })}
    </div>
  );
}
